import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy } from 'lucide-react';

const leaderboard = [
  { rank: 1, name: 'Aisha Patel', avatar: 'A', assignments: 8, quizAvg: 96, xp: 1240, badge: '🥇' },
  { rank: 2, name: 'Rahul Desai', avatar: 'R', assignments: 8, quizAvg: 92, xp: 1180, badge: '🥈' },
  { rank: 3, name: 'Sneha Iyer', avatar: 'S', assignments: 7, quizAvg: 89, xp: 1050, badge: '🥉' },
  { rank: 4, name: 'Karan Joshi', avatar: 'K', assignments: 7, quizAvg: 85, xp: 990, badge: null },
  { rank: 5, name: 'Priya Nair', avatar: 'P', assignments: 6, quizAvg: 88, xp: 960, badge: null },
  { rank: 6, name: 'Arjun Mehta', avatar: 'A', assignments: 6, quizAvg: 82, xp: 910, badge: null },
  { rank: 7, name: 'You', avatar: 'Y', assignments: 5, quizAvg: 86, xp: 870, badge: null, isYou: true },
  { rank: 8, name: 'Vikram Singh', avatar: 'V', assignments: 5, quizAvg: 78, xp: 820, badge: null },
  { rank: 9, name: 'Ananya Rao', avatar: 'A', assignments: 4, quizAvg: 80, xp: 760, badge: null },
  { rank: 10, name: 'Ravi Kumar', avatar: 'R', assignments: 4, quizAvg: 74, xp: 720, badge: null },
];

const rankColors = {
  1: 'from-yellow-400 to-amber-500',
  2: 'from-slate-400 to-slate-500',
  3: 'from-amber-600 to-amber-700',
};

export default function LeaderboardSection() {
  const [sortBy, setSortBy] = useState('xp');
  const sorted = [...leaderboard].sort((a, b) => {
    if (sortBy === 'xp') return b.xp - a.xp;
    if (sortBy === 'assignments') return b.assignments - a.assignments;
    return b.quizAvg - a.quizAvg;
  }).map((s, i) => ({ ...s, currentRank: i + 1 }));

  const top3 = sorted.slice(0, 3);
  const rest = sorted.slice(3);
  const maxXp = leaderboard[0].xp;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Leaderboard</h2>
          <p className="text-muted-foreground text-sm mt-1">Top performers in your batch</p>
        </div>
        <div className="flex gap-2">
          {['xp', 'assignments', 'quizAvg'].map(s => (
            <button key={s} onClick={() => setSortBy(s)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all ${sortBy === s ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25' : 'glass border border-black/10 text-muted-foreground hover:text-foreground'}`}>
              {s === 'quizAvg' ? 'Quiz Score' : s === 'xp' ? 'XP Points' : 'Assignments'}
            </button>
          ))}
        </div>
      </div>

      {/* Top 3 Podium */}
      <div className="grid grid-cols-3 gap-4 items-end">
        {/* 2nd */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="glass rounded-2xl border border-white/80 p-4 text-center order-1">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center mx-auto mb-2 shadow-lg text-white font-heading font-black text-xl">
            {top3[1]?.avatar}
          </div>
          <p className="text-[10px] text-muted-foreground">🥈 2nd</p>
          <p className="font-heading font-bold text-xs mt-0.5 truncate">{top3[1]?.name}</p>
          <p className="text-xs text-primary font-bold mt-1">{top3[1]?.xp} XP</p>
        </motion.div>
        {/* 1st */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="glass-yellow rounded-2xl border border-primary/30 p-4 text-center order-2 shadow-lg shadow-primary/15">
          <motion.div animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.5, repeat: Infinity }}
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center mx-auto mb-2 shadow-xl text-white font-heading font-black text-2xl">
            {top3[0]?.avatar}
          </motion.div>
          <div className="flex items-center justify-center gap-1 mb-1">
            <Trophy className="w-3.5 h-3.5 text-primary" />
            <p className="text-[10px] text-primary font-bold">1st Place</p>
          </div>
          <p className="font-heading font-bold text-sm truncate">{top3[0]?.name}</p>
          <p className="text-sm text-primary font-black mt-1">{top3[0]?.xp} XP</p>
        </motion.div>
        {/* 3rd */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="glass rounded-2xl border border-white/80 p-4 text-center order-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center mx-auto mb-2 shadow-lg text-white font-heading font-black text-xl">
            {top3[2]?.avatar}
          </div>
          <p className="text-[10px] text-muted-foreground">🥉 3rd</p>
          <p className="font-heading font-bold text-xs mt-0.5 truncate">{top3[2]?.name}</p>
          <p className="text-xs text-primary font-bold mt-1">{top3[2]?.xp} XP</p>
        </motion.div>
      </div>

      {/* Full Table */}
      <div className="glass rounded-2xl border border-white/80 overflow-hidden">
        <div className="grid grid-cols-[2rem_1fr_auto_auto_auto] gap-3 px-4 py-2.5 border-b border-black/8 text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <span>#</span>
          <span>Student</span>
          <span className="text-center hidden sm:block">Assignments</span>
          <span className="text-center hidden sm:block">Quiz Avg</span>
          <span className="text-right">XP</span>
        </div>
        {sorted.map((s, i) => (
          <motion.div key={s.name} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 + 0.3 }}
            className={`grid grid-cols-[2rem_1fr_auto_auto_auto] gap-3 px-4 py-3 items-center border-b border-black/5 last:border-0 transition-colors ${s.isYou ? 'bg-primary/8 border-l-2 border-l-primary' : 'hover:bg-black/[0.02]'}`}>
            <span className={`font-heading font-black text-sm ${s.currentRank <= 3 ? 'text-primary' : 'text-muted-foreground'}`}>{s.currentRank}</span>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white font-heading font-bold text-xs flex-shrink-0 ${s.currentRank <= 3 ? `bg-gradient-to-br ${rankColors[s.currentRank]}` : 'bg-secondary/70'}`}>
                {s.avatar}
              </div>
              <span className={`font-heading font-semibold text-sm truncate ${s.isYou ? 'text-primary' : ''}`}>
                {s.name}{s.isYou && <span className="text-[10px] ml-1 text-primary font-bold">(You)</span>}
              </span>
            </div>
            <span className="text-xs font-semibold text-center hidden sm:block">{s.assignments}/8</span>
            <span className={`text-xs font-bold text-center hidden sm:block ${s.quizAvg >= 90 ? 'text-green-600' : s.quizAvg >= 80 ? 'text-primary' : 'text-foreground'}`}>{s.quizAvg}%</span>
            <div className="flex flex-col items-end gap-0.5">
              <span className="font-heading font-black text-sm text-foreground">{s.xp}</span>
              <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${(s.xp / maxXp) * 100}%` }}
                  transition={{ duration: 0.6, delay: i * 0.04 + 0.5 }}
                  className={`h-full rounded-full ${s.isYou ? 'bg-primary' : 'bg-secondary/40'}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}